import { computed, ref } from 'vue'

import type { FunctionalComponent } from 'vue'

const KEY_TEST_MODE = '__USE_TEST_MODE'

type TestStage = 'alpha'
type TestMode = Record<TestStage, boolean>

const INITIAL_VALUE: TestMode = Object.freeze({
  alpha: false,
})

// 元々の実装では @vueuse/core の useLocalStorage を使っていたが、
// 一部の画面で useLocalStorage を使った実装を読み込むと、ブラウザが固まることがあった
// 対応として useLocalStorage を使わない実装に変更した
// useLocalStorage を使ったほうが可読性は高いと思うので、
// @vueuse/core のバージョンがいくつか上がった頃に実装を戻してみるか、ブラウザが固まる原因が分かれば対応してほしい

const initialValue = (() => {
  const savedRawValue = localStorage.getItem(KEY_TEST_MODE)
  if (savedRawValue == null) return INITIAL_VALUE

  try {
    return JSON.parse(savedRawValue) as TestMode
  } catch (e) {
    console.error(e)
    return INITIAL_VALUE
  }
})()

const settings = ref({ ...initialValue })

const computeEnabledStage = (settings: Readonly<TestMode>): TestMode => ({
  alpha: settings.alpha,
})

export const useTestMode = () => {
  const toggleAlpha = () => {
    const newValue = {
      ...settings.value,
      alpha: !settings.value.alpha,
    }

    settings.value = newValue
    localStorage.setItem(KEY_TEST_MODE, JSON.stringify(newValue))
  }

  const enabled = computed((): TestMode => computeEnabledStage(settings.value))

  return {
    enabled,
    toggleAlpha,
  }
}

export const AlphaFeature: FunctionalComponent = (_, { slots }) =>
  useTestMode().enabled.value.alpha ? slots.default?.() : null

// for non-Composition-API
// and not reactive
export const getReadonlyTestMode = (): Readonly<TestMode> => {
  const stored = localStorage.getItem(KEY_TEST_MODE)
  const settings =
    stored != null ? (JSON.parse(stored) as TestMode) : INITIAL_VALUE
  return computeEnabledStage(settings)
}
